/**
 * Handles the user selection of a vector feature on the map.
 */

import get from 'lodash/get'
import has from 'lodash/has'
import isNil from 'lodash/isNil'
import isObject from 'lodash/isObject'
import set from 'lodash/set'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import GetDisplayProperties from 'data/GetDisplayProperties'
import GetPropertiesAll from 'data/GetPropertiesAll'
import GetPropertiesId from 'data/GetPropertiesId'
import GetPropertiesIdKey from 'data/GetPropertiesIdKey'
import GetPropertiesLabel from 'data/GetPropertiesLabel'
import PoziFragments from 'drawer/tabs/info/fragment/fragments'
import { WHATS_HERE_LAYER_TITLEID } from 'drawer/tabs/info/WhatsHereUtils'
import GetGeometryProperties from 'map/GetGeometryProperties'
import highlightVectorFeature from 'map/HighlightVectorFeature'
import { targetSetOLFeature } from 'utils/target'

import { setCurrentTarget } from './actions'

export default function selectVectorFeature (olFeature, olLayer, highlight = true, isFromSearch = false) {
  const app = PoziUtils.getApp()

  if (isNil(olFeature) || isNil(olLayer) || !isObject(olFeature) || !isObject(olLayer)) {
    console.log('SelectVectorFeature passed invalid olFeature or olLayer.')
    return
  }

  const layerConf = PoziUtils.getLayerFromOLLayer(olLayer)
  if (isNil(layerConf)) {
    console.log('SelectVectorFeature Invalid layer!')
    return
  }

  // console.debug('selectVectorFeature', olFeature, olLayer, highlight)

  const preventClick = get(layerConf, 'preventClick', false)
  if (preventClick) {
    console.log('SelectVectorFeature preventClick', layerConf.title)
    return
  }

  console.log('SelectVectorFeature layer title', layerConf.title)

  const activeTabId = Model.get('app.drawer.activetabid', null)
  if (activeTabId !== 'info') {
    Model.set('app.drawer.activetabid', 'info')
  }

  const geomProperties = GetGeometryProperties(olFeature)
  const featureCollectionCentroid = geomProperties.$centroid
  if (olFeature.setProperties) {
    olFeature.setProperties(geomProperties, true)
  }

  // TODO: Need to move this functionality to Redux.
  // This causes the 'feature' to be set in the state and therefore reflected in the URL hash
  targetSetOLFeature(olFeature, layerConf)

  // Highlight selected vector feature
  if (highlight) {
    highlightVectorFeature(olFeature, olLayer)
  }

  // DEPRECATED...
  // Find the ID of the clicked vector olFeature if not already set.
  if (!has(layerConf, 'config.spatial.id')) {
    set(layerConf, 'config.spatial.id', GetPropertiesIdKey(layerConf, olFeature.getProperties()))
  }

  const hideDetailsOnInfoPanel = get(layerConf, 'hideDetailsOnInfoPanel', false)

  let featureIdValue = null
  try {
    featureIdValue = PoziUtils.getFeaturePropertyValueCaseInsensitive(olFeature, layerConf.config.spatial.id) || olFeature.getId()
    app.selectMarker.vector = { id: featureIdValue }
  } catch (err) {
    console.log('SelectVectorFeature unable to find valid feature id... continue?')
  }

  // Do not report 'feature clicked' event if the selected feature is What's Here
  if (layerConf.titleid !== WHATS_HERE_LAYER_TITLEID) {
    const additionalInfo = featureIdValue ? `id value: ${featureIdValue}` : undefined

    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: layerConf.title,
      action: 'Feature clicked',
      additionalInfo
    })
  }

  const properties = olFeature.getProperties()
  const propertiesVisible = GetDisplayProperties(layerConf, properties)
  const propertiesAll = GetPropertiesAll(properties)
  const propertiesLabel = GetPropertiesLabel(layerConf, properties)
  const propertiesId = GetPropertiesId(layerConf, properties)
  const propertiesIdKey = GetPropertiesIdKey(layerConf, properties)
  const titleid = layerConf.titleid

  // TODO: make this dependent on the projection of the layer
  const fcConvertOptions = {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857'
  }

  const fc = PoziUtils.convertOLFeatureArrToTurfFeature([olFeature], fcConvertOptions)

  // This is the combined result of line/polygon select + any (ctrl-) click targets
  const featureCollectionOriginal = Model.get('app.map.click.controlkeyfc', null)

  // Keeping track of child targets helps a ChildPanel decide if its panels should be open or closed
  const isChildTarget = false

  // IF YOU UPDATE THIS THEN ALSO UPDATE IN ChildPanelLayerItems.js
  window.store.dispatch(
    setCurrentTarget({
      titleid,
      hideDetailsOnInfoPanel,
      isChildTarget,
      isFromSearch,
      propertiesIdKey,
      propertiesId,
      propertiesLabel,
      propertiesVisible,
      propertiesAll,
      featureCollectionCentroid,
      featureCollection: fc,
      featureCollectionOriginal
    })
  )

  PoziFragments().load({
    titleid,
    layer: layerConf,
    featureid: featureIdValue
  })
}
